import { useCallback, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { find } from "lodash";

import { getErrorMessage, getStatus } from "./utils";

import { FormValidationMethods } from "@/components/Form/types";
import {
  BaseAdapterProps,
  LabelConfig,
  UseFieldHandlersOptions,
  UseFieldStateOptions,
  UseFormIntegrationOptions,
} from "./types";
import { InputTooltipHelper } from "@/utils/inputTooltipHelper";

export const useFormIntegration = ({
  name,
  elementRef,
  defaultValue,
  externalValue,
  rules,
}: UseFormIntegrationOptions) => {
  const methods: FormValidationMethods = useFormContext();
  const registeredRef = useRef(false);

  const watchedValue = useWatch({
    name,
    control: methods?.control,
  });

  useEffect(() => {
    if (!methods) return;

    const element = elementRef.current?.input || elementRef.current?.textarea || elementRef.current;

    if (!element || !name) return;

    const schemaRules = methods.schema && find(methods.schema, { name });

    const finalRules: any = {
      ...(schemaRules?.rules || {}),
      ...(rules || {}),
    };

    methods.register(name, finalRules);

    // Fix focus error
    if (methods.control._fields[name]) {
      (methods.control._fields[name] as any)._f.ref = element;
    }

    registeredRef.current = true;
  }, [name, methods, elementRef]);

  useEffect(() => {
    if ((defaultValue !== undefined || externalValue !== undefined) && registeredRef.current) {
      methods.setValue(name, defaultValue || externalValue);
    }
  }, []);

  return { methods, watchedValue };
};

export const useFieldState = ({
  name,
  error,
  errorMessage,
  success,
  loading,
  disabled,
  value: externalValue,
  defaultValue,
  methods,
  watchedValue,
}: UseFieldStateOptions & { methods: FormValidationMethods; watchedValue: any }) => {
  const currentValue =
    externalValue !== undefined ? externalValue : (watchedValue ?? defaultValue ?? "");
  const status = getStatus(error, errorMessage, success);

  const actualErrorMessage = errorMessage || methods?.formState?.errors?.[name];
  const finalErrorMessage: React.ReactNode = actualErrorMessage
    ? getErrorMessage(actualErrorMessage)
    : undefined;

  const isDisabled = disabled || loading;

  return {
    currentValue,
    status,
    finalErrorMessage,
    isDisabled,
  };
};

export const useFieldHandlers = <T extends HTMLInputElement | HTMLTextAreaElement>({
  name,
  methods,
  onChange,
  onBlur,
}: UseFieldHandlersOptions<T>) => {
  const handleChange = (e: React.ChangeEvent<T>) => {
    if (methods?.formState.errors[name]) {
      methods.clearErrors(name);
    }

    methods?.setValue(name, e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = useCallback(
    (e: React.FocusEvent<T>) => {
      methods?.trigger(name);
      onBlur?.(e);
    },
    [name, methods, onBlur]
  );

  return { handleChange, handleBlur };
};

export const useAdapterLogic = <
  TElement extends HTMLInputElement | HTMLTextAreaElement,
  TProps extends BaseAdapterProps & LabelConfig,
>(
  props: TProps & {
    value?: any;
    defaultValue?: any;
    onChange?: (e: React.ChangeEvent<TElement>) => void;
    onBlur?: (e: React.FocusEvent<TElement>) => void;
    disabled?: boolean;
    id?: string;
  },
  elementRef: React.RefObject<any>
) => {
  const {
    name,
    error,
    errorMessage,
    success,
    loading,
    disabled,
    onChange,
    defaultValue,
    value: externalValue,
    onBlur,
    helper,
    rules,
    ...restPropsWithLabels
  } = props;

  const { methods, watchedValue } = useFormIntegration({
    name,
    elementRef,
    defaultValue,
    externalValue,
    rules,
  });

  const fieldState = useFieldState({
    name,
    error,
    errorMessage,
    success,
    loading,
    disabled,
    value: externalValue,
    defaultValue,
    methods,
    watchedValue,
  });

  const { handleChange, handleBlur } = useFieldHandlers({
    name,
    methods,
    onChange,
    onBlur,
  });

  const { label, ...restProps } = restPropsWithLabels;

  const labelProps: LabelConfig = (() => {
    if ("label" in props && props.label !== undefined) {
      return {
        label: props.label,
        ...("ariaLabel" in props && props.ariaLabel ? { ariaLabel: props.ariaLabel } : {}),
      } as { label: string | React.ReactNode; ariaLabel?: string };
    } else {
      return { ariaLabel: props.ariaLabel } as { label?: never; ariaLabel: string };
    }
  })();

  if (props.helper && labelProps.label) {
    const labelWithTooltip = (
      <div className="u-flex u-flex-row">
        {label}
        {
          <div className="u-grey u-margin-horizontal">
            {InputTooltipHelper({ helper, id: props.id })}
          </div>
        }
      </div>
    );

    labelProps.label = labelWithTooltip;
  }

  const isExternallyControlled = externalValue !== undefined;

  const commonProps = {
    ref: elementRef,
    name,
    ...(isExternallyControlled
      ? { value: externalValue }
      : { defaultValue: fieldState.currentValue }),
    status: fieldState.status,
    errorMessage: fieldState.finalErrorMessage,
    disabled: fieldState.isDisabled,
    isLoading: loading,
    onChange: handleChange,
    onBlur: handleBlur,
    ...restProps,
    ...labelProps,
    data_qa: undefined,
  };

  return commonProps;
};
